<template>
  <div class="text-center" v-if="step == 1">
    <div class="title mt-5 mb-2">
      {{$t('Копия страницы')}}
    </div>
    <div class="d-flex justify-center text-center mb-4">
      <div class="copy-description">
        {{$t('Введите название, которое будет в адресе скопированной страницы')}}
      </div>
    </div>

    <div class="d-flex flex-column ga-2">
      <v-text-field
          ref="inputRef"
          variant="outlined"
          :placeholder="$t('название страницы')"
          :error-messages="$t(errorMessage)"
          :hide-details="!error"
          autofocus
          :prefix="`${host}/`"
          density="compact"
          rounded="lg"
          bg-color="#fff"
          @input="name = name.replace(/\s/g, '').toLowerCase()"
          v-model="name"
          class="name-field"
          @update:model-value="checkAlias"
          clearable
          maxlength="30"
      >
        <template v-slot:clear>
          <v-hover v-if="!nameLoading">
            <template v-slot:default="{ props }">
              <v-icon :icon="ClearIcon" v-bind="props" @click="name = '';clearError()" v-if="!!variants.length || error"/>
            </template>
          </v-hover>
        </template>
        <template v-slot:append-inner>
          <v-fade-transition leave-absolute>
            <v-icon :icon="SuccessIcon" size="20" v-if="name && !variants.length && !nameLoading && !error"/>
            <v-progress-circular v-else-if="nameLoading" size="24" :width="2" indeterminate color="primary" bg-color="transparent"/>
          </v-fade-transition>
        </template>
        <template #message>
          <div class="error-text">
            <template v-if="errorCode != 403">
              {{ $t(errorMessage) }}
            </template>
            <i18n-t keypath="В адресе есть недопустимые слова. Чтобы использовать этот адрес, свяжитесь со " v-else>
              <template #support>
                <span @click="openRocket" class="link">
                  {{ $t('службой поддержки') }}
                </span>
              </template>
            </i18n-t>
          </div>
        </template>
        <template #details>
          <v-menu v-model="showMenu" v-if="!!variants.length" :target="inputRef">
            <div class="variants">
              <div v-for="variant in variants" class="variant" @click="updateName(variant)">
                <div>{{variant}}</div>
                <v-icon :icon="SuccessIcon" size="20"/>
              </div>
            </div>
          </v-menu>
        </template>
      </v-text-field>

      <v-btn variant="flat" color="primary" rounded="lg" :disabled="progress" class="create-button"
             @click="checkAuthAndCreatePage()">
        <template v-if="progress">
          <v-progress-circular indeterminate size="18" :width="2" color="white" bg-color="transparent" class="mr-2"/>
          {{ $t('Копируем...') }}
        </template>
        <template v-else>
          {{$t('Скопировать')}}
        </template>
      </v-btn>
    </div>
  </div>
  <div class="text-center mb-5" v-else>
    <v-icon :icon="SuccessIcon" size="40"/>
    <div class="title mt-2 mb-2">
      {{$t('Готово!')}}
    </div>
    <div class="d-flex justify-center text-center mb-4">
      <div class="delete-description">
        {{$t('Копия страницы создана')}}
      </div>
    </div>
    <div>
      <v-btn variant="flat" color="primary" @click="goEdit()" rounded="lg" class="w-100 mb-2">
        {{$t('Открыть копию')}}
      </v-btn>
    </div>
    <div>
      <v-btn variant="flat" color="secondary" @click="goBack()" rounded="lg" class="w-100">
        {{$t('Остаться здесь')}}
      </v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">

import {onMounted, ref} from "vue";
import {useDialogStore} from "@/stores/dialogStore";
import {useTapStore} from '@/stores/tapStore';
import { useRoute, useRouter } from 'vue-router'
import ClearIcon from "~/assets/icons/clear.vue";
import SuccessIcon from "~/assets/icons/success.vue";
import Copy from "@/components/dialogs/Copy.vue";

const route = useRoute()
const router = useRouter();
const config = useRuntimeConfig()
const host = config.public.tapLink;

const props = defineProps({
  id: { type: Number },
  name: { name: String, default: '' },
  new: { name: Boolean, default: true }
})

const errorMessage = ref('')

const tapStore = useTapStore();

const dialogStore = useDialogStore()

const step = ref(1)
const name = ref('')
const nameLoading = ref(false)
const variants = ref<string[]>([])
const error = ref(false)
const showMenu = ref(false)
const errorCode = ref(0)
const inputRef = ref(null)
const progress = ref(false)

const openRocket = () => {
  //@ts-nocheck
  RocketChat(function() {
    this.showWidget();
    this.maximizeWidget();
  });
}

function goEdit() {
  tapStore.setTap()
  const profileName = name.value.replace(/\s/g, '').toLowerCase()
  router.push(`/profile/${profileName}/edit`)
  dialogStore.close(Copy)
}

const checkAlias = () => {
  if(!name.value) {
    clearError()
    return
  }

  nameLoading.value = true
  tapStore.checkAlias(name.value.replace(/\s/g, '').toLowerCase()).then(result => {
    variants.value = result;
    error.value = result.length > 0
    showMenu.value = error.value
    errorMessage.value = error.value ? 'Такой адрес уже используется' : ''
  }).catch(e => {
    variants.value = []
    error.value = true
    if(e.status == 400) {
      errorMessage.value = 'Ссылка может содержать от 3 до 30 символов и состоять из латинских букв, цифр и символов «.»и «_»'
      errorCode.value = e.status
    }
    if(e.status == 403) {
      errorMessage.value = 'error'
      errorCode.value = e.status
    }
  }).finally(() => {
    nameLoading.value = false
  })
}

const clearError = () => {
  error.value = false
  errorMessage.value = ''
  errorCode.value = 0
  variants.value = []
}

const checkAuthAndCreatePage = () => {
  if(error.value) {
    return
  }
  copyPage()
}



const copyPage = () => {
  if(!name.value) {
    clearError()
    variants.value = []
    error.value = true
    errorMessage.value = 'Ссылка может содержать от 3 до 30 символов и состоять из латинских букв, цифр и символов «.»и «_»'
    errorCode.value = 400
    return
  }

  progress.value = true
  tapStore.copyTap(name.value.replace(/\s/g, '').toLowerCase(), props.id).then(() => {
    step.value = 2;
    tapStore.getTapList()

  })
}

function goBack() {
  dialogStore.close(!route.path.includes('/edit'))
}
const updateName = (variant: any) => {
  name.value = variant.replace(/\s/g, '').toLowerCase()
  checkAlias()
}
onMounted(() => {
  if(props.name) {
    name.value = props.name.replace(/\s/g, '').toLowerCase()
    copyPage()
  }
  inputRef.value?.focus()
})

</script>

<style scoped lang="sass">
.title
  color: #363636
  text-align: center
  font-size: 20px
  font-weight: 600
  line-height: normal
.copy-description
  color: #363636
  text-align: center
  font-size: 14px
  font-weight: 400
  line-height: normal
  max-width: 280px
.name-field
  ::v-deep
    .v-input__control
      .v-text-field__prefix
        color: #363636
        opacity: 1
        font-size: 14px
        font-weight: 400
        line-height: 20px

      .v-field__input
        padding-inline: 0 var(--v-field-padding-end)
.variants
  border-radius: 8px
  border: 1px solid #CED4DA
  padding: 4px 0
  background: #FFF
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.12)
  .variant
    padding: 10px 16px
    gap: 8px
    display: flex
    justify-content: space-between
    color: #363636
    font-size: 14px
    line-height: 20px
    &:hover
      background: #F0F1F5
      cursor: pointer
.bg-secondary
  border: 1px solid #CED4DA
</style>
